import os from 'os';

import _ from 'lodash';

import { deepFreeze, DEVMODE } from './util';
import Colors from './colors';
import { CURRENT_EXPANSION } from './constants';

// Per-worktree dev slot (see scripts/dev/slot.js). Offsets the dev listen ports
// so multiple `yarn dev` stacks coexist. Guarded for the browser bundle, where
// `process` may be undefined and RAIDBOTS_SLOT is never defined — so this reads
// 0 there, which is correct: the frontend uses relative URLs and never consults
// these ports. (Deliberately NOT injected via webpack DefinePlugin.)
//
// This is the ONE place that re-derives the port formulas instead of calling
// slot.js's computePorts: shared/config.js is isomorphic and can't require the
// node-only slot.js into the browser bundle (run.js, webpack.config.js,
// cypress.config.js all import computePorts directly). The formulas below MUST
// stay in sync with scripts/dev/slot.js computePorts.
const SLOT = Number(
  (typeof process !== 'undefined' && process.env && process.env.RAIDBOTS_SLOT) || 0,
);
const slotOffset = SLOT * 100;
// Browsers scope cookies by hostname and ignore the port, so every dev stack on
// localhost/dev.raidbots.com would otherwise share one `raidsid` — while each
// slot has its own sessions Redis. Logging into one stack overwrites the cookie
// and logs you out of the others. Suffix the slot in dev to keep them separate.
// Slot 0 is `raidsid_0` whether or not RAIDBOTS_SLOT is set: its web runs in
// Docker (which doesn't set it) but its host-web opt-in does set it to "0".
const sessionCookieName = DEVMODE ? `raidsid_${SLOT}` : 'raidsid';
// Dev web port: slot 0 keeps the privileged 443; worktrees use non-privileged
// 8443+slot. Precomputed to avoid a nested ternary in the port block below.
const devWebPort = SLOT === 0 ? 443 : 8443 + SLOT;

const MINUTE = 60 * 1000;

const DEFAULT_TIME_LIMIT = 15 * MINUTE;
const DEFAULT_PATRON_TIME_LIMIT = DEFAULT_TIME_LIMIT * 1;

const DEFAULT_ITERATIONS_LIMIT = 300000;

const DEFAULT_COPY_ACTOR_LIMIT = 60;
const MAX_COPY_ACTORS = 200;

const HIGH_PRECISION = 0.05;
const LOW_PRECISION = DEVMODE ? 0.25 : 0.1;

const MILLION = 1000000;

export default deepFreeze({
  currentExpansion: CURRENT_EXPANSION,

  sessionCookieName,

  wowEnvs: [{
    key: 'live',
    wowKey: 'retail',
    product: 'wow',
    name: 'Retail',
    wowheadSubdomain: 'www',
    color: Colors.green,
    simcBranch: 'midnight',
    raidbotsBranch: 'main',
  }, {
    key: 'ptr',
    wowKey: 'ptr',
    product: 'wowt',
    name: 'PTR 1',
    wowheadSubdomain: 'ptr',
    color: Colors.orange,
    simcBranch: 'midnight',
    raidbotsBranch: 'main',
  }, {
    key: 'xptr',
    wowKey: 'xptr',
    product: 'wowxptr',
    name: 'PTR 2',
    wowheadSubdomain: 'ptr-2',
    color: Colors.orange,
    simcBranch: 'midnight',
    raidbotsBranch: 'main',
  }, {
    key: 'beta',
    wowKey: 'beta',
    product: 'wow_beta',
    name: 'Beta',
    wowheadSubdomain: 'beta',
    color: Colors.red,
    simcBranch: 'midnight',
    raidbotsBranch: 'main',
  }],

  wowEnvMap: {
    live: 'live',
    test: 'ptr',
    // mimiron/web-beta (`./deploy/web beta`) deploys the ptr entry's
    // raidbotsBranch and serves test (ptr) game data via config.beta.wowEnv.
    // While beta shares ptr with test, DBCache-triggered updates are tagged
    // 'test' not 'beta' — see SIMC_ENV_MAP in jobserver/jobs/dbcache.js.
    beta: 'ptr',
  },

  links: {
    support: 'mailto:support@raidbots.com',
    supportSite: 'https://support.raidbots.com/',
    discord: 'https://discord.gg/86EF64Q',
    unsubscribe: 'https://support.raidbots.com/article/14-can-i-unsubscribe-from-raidbots-emails',
    simcAddon: 'https://www.curseforge.com/wow/addons/simulationcraft',
  },

  // Listen ports. In dev, each slot offsets the service cluster by slot*100 and
  // web by slot (slot 0 keeps 443; worktrees use the non-privileged 8443+slot).
  // Prod is unaffected (slot is always 0 there).
  port: {
    http: {
      worker: 4414 + slotOffset,
      web: DEVMODE ? devWebPort : 8080,
      // Dev webpack-dev-server port (stride 10, see scripts/dev/slot.js). The web
      // server injects this into the dev HTML templates so the frontend bundle
      // <script> + HMR hit this slot's webpack, not slot 0's.
      webpack: DEVMODE ? 8081 + SLOT * 10 : 8081,
      queueUI: 3000 + slotOffset,
      flightmaster: 4415 + slotOffset,
      jobserver: 4416 + slotOffset,
      discordbot: 4417 + slotOffset,
    },
  },

  features: {
    armory: true,
  },

  beta: {
    simcVersion: 'latest',
    features: {
      armory: true,
      inputValidation: false,
    },
    forcePtr: true,
    wowEnv: 'test',
  },

  legal: {
    touVersion: new Date('26 Oct 2023 12:00:00 PST'),
  },

  flightmaster: {
    // how many jobs should a single flightmaster manage
    jobsPerProcess: DEVMODE ? 2 : 3,

    // expire time for simc redis logs
    simcLogTtl: 60 * 60 * 6,

    // how many log lines to keep
    simcLogLength: 10,

    // default worker concurrency
    defaultConcurrency: 1,

    // flight timeout is set a bit higher than job timeout to try to let worker's handle timeouts in non-chunk cases
    flightTimeoutMultiplier: 1.1,

    // restart chunk if it has not started after this much time
    chunkRestart: 30 * 1000,

    // target_error >= this level will use profileset_work_threads
    psetWorkThreadThreshold: 0.5,

    // value for profileset_work_threads
    psetWorkThreadAmount: 2,

    psetAnalyzeErrorInterval: 50,

    // threshold of psets to report to keep JSON size down
    largeProfilesetCount: DEVMODE ? 250 : 250,

    // droptimizer reports keep more: the panel's per-encounter EV/Best compute
    // from the actors present in data.json, so culling silently skews EV — and
    // the bonus-roll combined source (raid + all M+) legitimately exceeds the
    // default cap
    largeProfilesetCountDroptimizer: DEVMODE ? 500 : 500,

    // switch to multistage when more than this number of psets
    multistageThreshold: 4,

    // make this available on the config
    highPrecision: HIGH_PRECISION,
    lowPrecision: LOW_PRECISION,

    // high precision multistage target errors to use
    multiPrecisionDefault: DEVMODE ? [
      2,
      0.4,
      HIGH_PRECISION,
    ] : [
      1,
      0.2,
      HIGH_PRECISION,
    ],

    // low precision multistage target errors to use
    multiPrecisionFast: DEVMODE ? [
      2,
      0.75,
      LOW_PRECISION,
    ] : [
      1,
      0.3,
      LOW_PRECISION,
    ],

    // target chunk sizes to use for each stage
    // actual chunk size may vary due to chunk balancing and concurrency
    // this is in reverse order from the errors above (first size is used for highest precision)
    // it's a bit annoying but it makes it a bit easier to play with more stage configs if desired
    chunkSizes: DEVMODE ? [8, 32, 256] : [8, 32, 256],

    // keep actors within (errorThreshold * dpsError * error)
    errorThreshold: 2.5,

    // used for tracking counts of queue times that are longer than desired
    longQueuePublic: 7 * 60 * 1000,
    longQueuePremium: 30 * 1000,
  },

  loggingThrottleMs: 200,

  worker: {
    numCores: DEVMODE ? (_.max([_.floor(os.cpus().length / 2) - 2, 0]) || 32) : 32,
    logRedisModulo: 3,
  },

  redis: {
    apiKeyJobsExpiry: 60 * 30,
    rateLimitKeyExpiry: 60 * 10,
    // first-sim priority boost throttle: at most one boost per user per window,
    // auto-resets if the flight-completion release is missed (see web/routes/sim.js)
    firstSimBoostExpiry: 60 * 15,
    // Great Vault deferred-boost marker (D9): the pair's parent follows the
    // declined companion within seconds; an abandoned marker must not linger.
    boostDeferredExpiry: 120,
  },

  // shows warnings/errors on input when input versions are too low
  addon: {
    simc: {
      minVersion: '12.1.0',
    },
    wow: {
      minVersion: '12.1.0',
    },
  },

  discord: {
    reward: {
      maxMemberCount: 100,
      largeGuildThreshold: 200, // for requiring admin role to assign rewards
    },
  },

  optimize: {
    // getRawCombinations bails out when the cartesian product is larger than this
    maxRawCombinations: 200000,

    // getCombinations bails out if getRawCombinations returns more than this value
    maxCombinations: 10000,

    // Top Gear download fails when getRawCombinations returns more than this
    maxCombinationsDownload: 10000,

    // getCombinations bails out at this number of combos
    maxTotalCombinations: 10000,

    // top end of iterations for non-smart sims
    smartThreshold: 2000000,

    maxWalkPartials: 5000000,

    maxContaminants: 9,

    // head/shoulder/feet enchants are tertiary-only (no DPS impact), so one pick is
    // enough for Top Gear to remind you to apply it without multiplying combinations
    maxEnhancements: {
      main_hand: 9,
      off_hand: 9,
      head: 1,
      shoulder: 1,
      back: 4,
      chest: 4,
      bracer: 4,
      glove: 4,
      legs: 4,
      feet: 1,
      finger: 5,
      wrist: 4,
      hands: 4,
      socket: 13,
    },

    // max number of combinations allowed when using replace existing gem/enchants
    //maxReplaceEnhancementCombinations: 3200, // 16m iterations
  },

  stripe: {
    publicKey: DEVMODE ? 'pk_test_jg3mWa7M0q7motw4y0hidxsn' : 'pk_live_rR7VnDnPTM5GIi4zYiWopUOM',

    // block premium for an account if they dispute this many times
    blockAfterDisputesCount: 5,
    // block premium for an account if they consistently have failed async payments
    blockAfterIncompleteExpired: 5,
  },

  account: {
    emailVerifyRateMinutes: 15,
    emailVerifyNagDays: 1,
  },

  // Queue configs
  queue: {
    simc: {
      attempts: 4,
      backoff: {
        delay: 2 * 1000,
        type: 'exponential',
      },
      removeOnComplete: false,
      // how long to wait for no response from worker before killing job
      ttl: 2 * 60 * 1000,
    },
    flight: {
      attempts: 9,
      backoff: {
        delay: 2 * 1000,
        type: 'exponential',
      },
      removeOnComplete: false,
      // how long to wait for no response from worker before killing job
      ttl: 60 * 1000,
      // Bound the enqueue op itself (createFlightJob), NOT a Bull job option:
      // far above a healthy enqueue (<50ms; ~700ms worst-case single-command
      // bursts) so it only trips on a real connection stall, at which point the
      // submit fails fast with a retryable 503 instead of holding a Cloud Run
      // concurrency slot until the ~60min request timeout (2026-07-17 incident).
      enqueueTimeout: 5 * 1000,
    },
    simcReport: {
      attempts: 5,
      backoff: {
        delay: 1 * 1000,
        type: 'exponential',
      },
      delay: 500,
      removeOnComplete: true,
      timeout: 10000,
    },
    simcInputValidate: {
      attempts: 0,
      timeout: 2000,
    },
    userDownload: {
      attempts: 5,
      timeout: 12 * 60 * 60 * 1000,
    },
    discordUserRefresh: {
      attempts: 5,
      timeout: 60 * 1000, // bee
      backoff: {
        delay: 2 * 1000,
        type: 'exponential',
      },
      delay: 1000,
      removeOnComplete: true,
    },
    dbcache: {
      attempts: 5,
      timeout: 60 * 1000, // bee
      backoff: {
        delay: 2 * 1000,
        type: 'exponential',
      },
      delay: 1000,
      removeOnComplete: true,
    },
    admin: {
      attempts: 5,
      timeout: 60 * 1000,
    },
    adminSimcBuild: {
      attempts: 3,
      timeout: 30 * 60 * 1000,
    },
    adminUpdateGameData: {
      // No retries, and a timeout above the builder VM's 60-minute cap. The
      // old 30-minute timeout with retries meant bee could declare a job dead
      // and start a second builder while the first was still running — a
      // failed data run is re-kicked by a human, not retried blind.
      //
      // This value feeds bee's .retries(), which counts RETRIES after the
      // first execution, not total attempts: 0 here means run exactly once.
      attempts: 0,
      timeout: 65 * 60 * 1000,
    },
  },

  iterationsLimit: DEFAULT_ITERATIONS_LIMIT,

  reports: {
    htmlComboLimit: 20,
  },

  dataDownload: {
    requestFrequency: DEVMODE ? 60 * 1000 : 24 * 60 * 60 * 1000,
  },

  // NOTE: apiTokens used to live here. This file is bundled into the browser by
  // webpack, so the Discord bot's secret was published to every visitor. Both
  // the secret and the descriptors now live server-side in web/apiTokens.js,
  // with the secret itself read from metadata redis.

  userLevels: {
    default: {
      level: 0,
      reportRetentionDays: 30,
      simSubmitsPerHour: 20,
      simCpuBudgetPerHour: 600,
      noAds: true,
      iterationsLimit: DEFAULT_ITERATIONS_LIMIT,
      advancedActorLimit: _.min([DEFAULT_COPY_ACTOR_LIMIT, MAX_COPY_ACTORS]),
      simTimeLimit: DEFAULT_TIME_LIMIT,
      queuePriority: 'low', // 10
      patronChannel: false,
      guildDiscordPriority: false,
      apiAccess: false,
      concurrency: 1,
      beta: false,
    },
    registered: {
      level: 10,
      reportRetentionDays: 30,
      simSubmitsPerHour: 24,
      simCpuBudgetPerHour: 900,
      noAds: true,
      iterationsLimit: DEFAULT_ITERATIONS_LIMIT,
      advancedActorLimit: _.min([DEFAULT_COPY_ACTOR_LIMIT, MAX_COPY_ACTORS]),
      simTimeLimit: DEFAULT_TIME_LIMIT,
      queuePriority: 'low', // 10
      patronChannel: false,
      guildDiscordPriority: false,
      apiAccess: false,
      concurrency: 1,
      beta: false,
    },
    uncommon: {
      level: 20,
      reportRetentionDays: 120,
      simSubmitsPerHour: 40,
      simCpuBudgetPerHour: 2400,
      patreonTitle: 'Uncommon',
      noAds: true,
      iterationsLimit: 1.8 * MILLION,
      stripeIterationsLimit: 2 * MILLION,
      advancedActorLimit: _.min([2 * DEFAULT_COPY_ACTOR_LIMIT, MAX_COPY_ACTORS]),
      simTimeLimit: 2 * DEFAULT_PATRON_TIME_LIMIT,
      queuePriority: 'normal', // 0
      patronChannel: true,
      guildDiscordPriority: false,
      color: '#1eff00',
      apiAccess: false,
      droptimizerAzerite: true,
      concurrency: 1,
      beta: false,
    },
    rare: {
      level: 30,
      reportRetentionDays: 120,
      simSubmitsPerHour: 40,
      simCpuBudgetPerHour: 2400,
      patreonTitle: 'Rare',
      noAds: true,
      iterationsLimit: 1.8 * MILLION,
      stripeIterationsLimit: 2 * MILLION,
      advancedActorLimit: _.min([2 * DEFAULT_COPY_ACTOR_LIMIT, MAX_COPY_ACTORS]),
      simTimeLimit: 2 * DEFAULT_PATRON_TIME_LIMIT,
      queuePriority: 'normal', // 0
      patronChannel: true,
      guildDiscordPriority: false,
      color: '#0070dd',
      apiAccess: false,
      droptimizerAzerite: true,
      concurrency: 1,
      beta: false,
    },
    heirloom: {
      level: 40,
      reportRetentionDays: 120,
      simSubmitsPerHour: 40,
      simCpuBudgetPerHour: 4800,
      patreonTitle: 'Heirloom',
      noAds: true,
      iterationsLimit: 5 * MILLION,
      stripeIterationsLimit: 5.5 * MILLION,
      advancedActorLimit: _.min([4 * DEFAULT_COPY_ACTOR_LIMIT, MAX_COPY_ACTORS]),
      simTimeLimit: 2 * DEFAULT_PATRON_TIME_LIMIT,
      queuePriority: 'normal', // 0
      patronChannel: true,
      guildDiscordPriority: false,
      color: '#00ccff',
      apiAccess: false,
      droptimizerAzerite: true,
      concurrency: 1,
      beta: false,
    },
    epic: {
      level: 50,
      reportRetentionDays: 120,
      simSubmitsPerHour: 40,
      simCpuBudgetPerHour: 9600,
      patreonTitle: 'Epic',
      noAds: true,
      iterationsLimit: 15 * MILLION,
      stripeIterationsLimit: 16 * MILLION,
      advancedActorLimit: _.min([8 * DEFAULT_COPY_ACTOR_LIMIT, MAX_COPY_ACTORS]),
      simTimeLimit: 4 * DEFAULT_PATRON_TIME_LIMIT,
      queuePriority: 'normal', // 0
      patronChannel: true,
      guildDiscordPriority: true,
      color: '#a335ee',
      apiAccess: false,
      droptimizerAzerite: true,
      concurrency: 2,
      beta: false,
    },
    legendary: {
      level: 60,
      reportRetentionDays: 120,
      simSubmitsPerHour: 150,
      simCpuBudgetPerHour: null,
      patreonTitle: 'Legendary',
      noAds: true,
      iterationsLimit: 15 * MILLION,
      stripeIterationsLimit: 16 * MILLION,
      advancedActorLimit: _.min([8 * DEFAULT_COPY_ACTOR_LIMIT, MAX_COPY_ACTORS]),
      simTimeLimit: 4 * DEFAULT_PATRON_TIME_LIMIT,
      queuePriority: 'normal', // 0
      patronChannel: true,
      guildDiscordPriority: true,
      color: '#ff8000',
      apiAccess: false,
      droptimizerAzerite: true,
      concurrency: 2,
      beta: false,
    },
    theorycrafter: {
      level: 70,
      reportRetentionDays: 365,
      simSubmitsPerHour: 150,
      simCpuBudgetPerHour: null,
      patreonTitle: 'Theorycrafter',
      noAds: true,
      iterationsLimit: 16 * MILLION,
      stripeIterationsLimit: 16 * MILLION,
      advancedActorLimit: _.min([8 * DEFAULT_COPY_ACTOR_LIMIT, MAX_COPY_ACTORS]),
      simTimeLimit: 8 * DEFAULT_PATRON_TIME_LIMIT,
      queuePriority: 'normal', // 0
      patronChannel: true,
      guildDiscordPriority: true,
      color: '#a84300',
      apiAccess: true,
      droptimizerAzerite: true,
      concurrency: 2,
      beta: false,
    },
    simcdev: {
      level: 80,
      reportRetentionDays: 365,
      simSubmitsPerHour: 150,
      simCpuBudgetPerHour: null,
      patreonTitle: 'SimC Dev',
      noAds: true,
      iterationsLimit: 16 * MILLION,
      stripeIterationsLimit: 16 * MILLION,
      advancedActorLimit: _.min([32 * DEFAULT_COPY_ACTOR_LIMIT, MAX_COPY_ACTORS]),
      simTimeLimit: 32 * DEFAULT_PATRON_TIME_LIMIT,
      queuePriority: 'normal', // 0
      patronChannel: true,
      guildDiscordPriority: true,
      color: '#f91590',
      apiAccess: true,
      droptimizerAzerite: true,
      concurrency: 2,
      beta: true,
    },
    artifact: {
      level: 100,
      reportRetentionDays: 365,
      simSubmitsPerHour: 150,
      simCpuBudgetPerHour: null,
      patreonTitle: 'Artifact',
      noAds: true,
      iterationsLimit: 32 * MILLION,
      stripeIterationsLimit: 32 * MILLION,
      advancedActorLimit: _.min([8 * DEFAULT_COPY_ACTOR_LIMIT, MAX_COPY_ACTORS]),
      simTimeLimit: 20 * DEFAULT_PATRON_TIME_LIMIT,
      queuePriority: 'normal', // 0
      patronChannel: true,
      guildDiscordPriority: true,
      color: '#e6cc80',
      apiAccess: true,
      droptimizerAzerite: true,
      concurrency: 2,
      beta: true,
    },
  },
});
